import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "../../../lib/api/client"
import { extractData } from "../../../lib/api/response-utils"
import { timeEntriesKeys } from "../../../lib/query-keys"
import type { ActiveTimer, TimerStartRequest } from "../types"

/**
 * Get the current user's active timer (if any).
 */
export function useActiveTimer() {
  return useQuery({
    queryKey: timeEntriesKeys.timer(),
    queryFn: async () => {
      const response = await api.timeEntries.timerActive()
      return extractData<ActiveTimer | null>(response)
    },
    staleTime: 30_000,
  })
}

/**
 * Start a new timer. Stops any existing timer first (server-side).
 */
export function useStartTimer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: TimerStartRequest) => api.timeEntries.timerStart(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.timer() })
    },
  })
}

/**
 * Stop the active timer, creating a time entry from it.
 */
export function useStopTimer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.timeEntries.timerStop(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.timer() })
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.mySummary() })
    },
  })
}
